import { Textarea } from "@/src/components/ui/textarea";
import { useToast } from "@/src/components/ui/use-toast";
import { useFetch } from "@/src/lib/useFetch";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import { useLoaderData, useNavigate } from "react-router-dom";
import { PatchRegionValues, PatchRegionSchema } from "../schemas";
import { Label } from "@/src/components/ui/label";
import { RelationMultiInput } from "../components/RelationMultiInput";
import { RelationSingleInput } from "../components/RelationSingleInput";
import { SuccessResponse, fetch } from "@/src/lib/fetch";
import { Country, Region, Town } from "../model";
import { Button } from "@/src/components/ui/button";
import { extractFromMultiName } from "../utils";
import { FormErrors } from "../components/FormErrors";

export async function regionLoader({ params }: { params: { regionId: string } }) {
  const response = await fetch<SuccessResponse<Region>>(`/api/locations/regions/${params.regionId}`);
  return { defaultValues: response.data, regionId: params.regionId };
}

export function EditRegion() {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { defaultValues, regionId } = useLoaderData() as {
    defaultValues: Partial<PatchRegionValues>;
    regionId: string;
  };

  const methods = useForm<PatchRegionValues>({
    resolver: zodResolver(PatchRegionSchema),
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const loadTowns = async (q: string) => {
    const data = await fetch<{ data: Town[] }>(
      `/api/locations/towns?page=1&pageSize=100&filter=${encodeURIComponent(q)}`,
    );
    return data.data;
  };

  const loadCountries = async (q: string) => {
    const data = await fetch<{ data: Country[] }>(
      `/api/locations/countries?page=1&pageSize=100&filter=${encodeURIComponent(q)}`,
    );
    return data.data;
  };

  const onSubmit = async (data: PatchRegionValues) => {
    try {
      await fetch(`/api/locations/regions/${regionId}`, "PATCH", data);
      toast({ title: "Success", description: "Region updated successfully" });
      navigate(`/locations/regions/${regionId}`);
    } catch (error) {
      toast({ title: "Error", description: "Failed to update region", variant: "destructive" });
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this region?")) {
      return;
    }

    try {
      await fetch(`/api/locations/regions/${regionId}`, "DELETE");
      toast({ title: "Success", description: "Region deleted successfully" });
      navigate("/locations");
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete region", variant: "destructive" });
    }
  };

  return (
    <div className="rounded-2xl shadow-md bg-white p-4">
      <h2 className="text-lg font-semibold">Edit Region - {extractFromMultiName(defaultValues.names)}</h2>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit, console.error)} className="space-y-4 p-4">
          <div>
            <Label>Names</Label>
            <Textarea {...register("names")} placeholder="Enter names, one per line" />
          </div>

          <div>
            <Label>Good to Know</Label>
            <Textarea {...register("goodToKnow")} placeholder="Good to know information" />
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea {...register("notes")} placeholder="Notes" />
          </div>

          <RelationMultiInput name="towns" label="Towns" loadOptions={loadTowns} />

          <RelationSingleInput name="country" label="Country" loadOptions={loadCountries} />

          <FormErrors errors={errors} />

          <Button type="submit">Save</Button>
          <Button className="ml-2" type="button" variant="destructive" onClick={onDelete}>
            Delete
          </Button>
        </form>
      </FormProvider>
    </div>
  );
}
